import React, { useCallback, useEffect, useState } from 'react';
import { Plus, MinusCircle, Save, Check, Search, CircleMinus, CircleCheck } from 'lucide-react';
import Skeleton from '../../common/Skeleton';
import FormControl from '../../common/FormControl';
import Modal from '../../common/Modal';
import Button from '../../common/Button';
import Pagination from '../../common/Pagination';
import Toast, { ToastMessage } from '../../common/Toast';
import { confirmWarning, confirmActive } from '../../common/confirm';
import NoDataFound from '../../common/NoDataFound';
import {
    ProcedureTemplate, RatingTemplateItem, RatingOptionItem,
    ratingTemplateService, ratingOptionService,
} from '../../../services/auditService';
import { extractApiError } from '../../../utils/apiError';

interface ProcedureRatingProps {
    procedure: ProcedureTemplate;
}

interface OptionDraft {
    key: number;
    id?: number;
    rating_option_text: string;
    rating_option_score: number;
}

interface FormState {
    rating_question: string;
    maximum_score: number;
    is_normalized: boolean;
    options: OptionDraft[];
}

const blankOption = (): OptionDraft => ({ key: Date.now() + Math.random(), rating_option_text: '', rating_option_score: 0 });
const blankForm = (): FormState => ({ rating_question: '', maximum_score: 5, is_normalized: false, options: [blankOption()] });

const ProcedureRating: React.FC<ProcedureRatingProps> = ({ procedure }) => {
    const [questions, setQuestions] = useState<RatingTemplateItem[]>([]);
    const [optionsByRating, setOptionsByRating] = useState<Record<number, RatingOptionItem[]>>({});
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [toggling, setToggling] = useState(false);
    const [isModalOpen, setModalOpen] = useState(false);
    const [currentQ, setCurrentQ] = useState<RatingTemplateItem | null>(null);
    const [formData, setFormData] = useState<FormState>(blankForm);
    const [toast, setToast] = useState<ToastMessage | null>(null);

    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => { setPage(1); }, [searchTerm, pageSize]);

    useEffect(() => {
        if (isModalOpen) {
            setSubmitting(false);
            setToggling(false);
        }
    }, [isModalOpen, currentQ]);

    const reload = useCallback(async () => {
        setLoading(true);
        try {
            const qs = await ratingTemplateService.listForProcedure(procedure.id);
            setQuestions(qs);
            const allOpts = await ratingOptionService.listAll();
            const buckets: Record<number, RatingOptionItem[]> = {};
            for (const opt of allOpts) {
                if (!buckets[opt.rating_template_id]) buckets[opt.rating_template_id] = [];
                buckets[opt.rating_template_id].push(opt);
            }
            setOptionsByRating(buckets);
        } catch (e: any) {
            setToast({ type: 'error', message: extractApiError(e, 'Failed to load rating questions.') });
        } finally {
            setLoading(false);
        }
    }, [procedure.id]);

    useEffect(() => { reload(); }, [reload]);

    const handleAdd = useCallback(() => {
        setCurrentQ(null);
        setFormData(blankForm());
        setModalOpen(true);
    }, []);

    const handleEdit = (q: RatingTemplateItem) => {
        const opts = optionsByRating[q.id] || [];
        setCurrentQ(q);
        setFormData({
            rating_question: q.rating_question,
            maximum_score: q.maximum_score ?? 5,
            is_normalized: !!q.is_normalized,
            options: opts.length
                ? opts.map(o => ({ key: o.id, id: o.id, rating_option_text: o.rating_option_text, rating_option_score: o.rating_option_score }))
                : [blankOption()],
        });
        setModalOpen(true);
    };

    const handleToggleActive = async () => {
        if (!currentQ) return;
        const isActive = (currentQ.is_active ?? 1) === 1;
        const next: 0 | 1 = isActive ? 0 : 1;
        const confirmFn = isActive ? confirmWarning : confirmActive;
        const ok = await confirmFn({
            title: next ? 'Reactivate this question?' : 'Inactivate this question?',
            text: next
                ? 'It will appear in active lists again.'
                : 'It will be hidden from active lists.',
            confirmText: next ? 'Yes, Reactivate' : 'Yes, Inactivate',
        });
        if (!ok) return;
        setToggling(true);
        try {
            await ratingTemplateService.update(currentQ.id, { is_active: next });
            setToast({ type: next ? 'success' : 'warning', message: next ? 'Reactivated' : 'Inactivated' });
            setModalOpen(false);
            await reload();
        } catch (e: any) {
            setToast({ type: 'error', message: extractApiError(e, 'Failed to update status.') });
        } finally {
            setToggling(false);
        }
    };

    const handleSave = async (e?: React.FormEvent) => {
        if (e) e.preventDefault();
        if (!formData.rating_question.trim()) return;
        const cleanOptions = formData.options.filter(o => o.rating_option_text.trim() !== '');
        if (cleanOptions.length === 0) { setToast({ type: 'warning', message: 'Add at least one rating option.' }); return; }
        setSubmitting(true);
        try {
            let ratingId: number;
            if (currentQ) {
                await ratingTemplateService.update(currentQ.id, {
                    rating_question: formData.rating_question,
                    maximum_score: formData.maximum_score,
                    is_normalized: formData.is_normalized,
                });
                ratingId = currentQ.id;
            } else {
                const created = await ratingTemplateService.create({
                    procedure_template_id: procedure.id,
                    rating_question: formData.rating_question,
                    maximum_score: formData.maximum_score,
                    is_normalized: formData.is_normalized,
                    is_active: 1,
                });
                ratingId = created.id;
            }
            const existing = currentQ ? (optionsByRating[currentQ.id] || []) : [];
            const keptIds = new Set(cleanOptions.filter(o => o.id != null).map(o => o.id as number));
            const toDelete = existing.filter(e => !keptIds.has(e.id));
            await Promise.all(toDelete.map(o => ratingOptionService.remove(o.id)));
            for (const opt of cleanOptions) {
                if (opt.id != null) {
                    const original = existing.find(e => e.id === opt.id);
                    if (!original || original.rating_option_text !== opt.rating_option_text || original.rating_option_score !== opt.rating_option_score) {
                        await ratingOptionService.update(opt.id, { rating_option_text: opt.rating_option_text, rating_option_score: opt.rating_option_score });
                    }
                } else {
                    await ratingOptionService.create({ rating_template_id: ratingId, rating_option_text: opt.rating_option_text, rating_option_score: opt.rating_option_score, is_active: 1 });
                }
            }
            setToast({ type: 'success', message: currentQ ? 'Rating question updated.' : 'Rating question created.' });
            setModalOpen(false);
            await reload();
        } catch (e: any) {
            setToast({ type: 'error', message: extractApiError(e, 'Save failed.') });
        } finally {
            setSubmitting(false);
        }
    };

    const addOption = () => setFormData(f => ({ ...f, options: [...f.options, blankOption()] }));
    const removeOption = (key: number) => setFormData(f => f.options.length > 1 ? ({ ...f, options: f.options.filter(o => o.key !== key) }) : f);
    const updateOption = (key: number, patch: Partial<OptionDraft>) =>
        setFormData(f => ({ ...f, options: f.options.map(o => o.key === key ? { ...o, ...patch } : o) }));

    const filtered = questions.filter(q => q.rating_question.toLowerCase().includes(searchTerm.toLowerCase()));
    const totalRecords = filtered.length;
    const paginatedRows = filtered.slice((page - 1) * pageSize, page * pageSize);

    return (
        <>
            <div className="netbody-header">
                <div className="flex items-center gap-2 ml-auto">
                    <FormControl.Input
                        type="text"
                        placeholder="Search…"
                        value={searchTerm}
                        onChange={e => setSearchTerm(e.target.value)}
                        leadingIcon={<Search size={14} />}
                        className="filter-search"
                        data-testid="INP_rating_template_search"
                    />
                    <Button variant="fill" icon={Plus} data-testid="BTN_rating_template_create" onClick={handleAdd}>Create</Button>
                </div>
            </div>

            <div className="table-list flex-1 min-h-0">
                <div className="table-list-scroll">
                    <table className="table-list-table">
                        <thead>
                            <tr>
                                <th className="table-list-th table-list-cell--sno">S.No</th>
                                <th className="table-list-th">Question</th>
                                <th className="table-list-th">Max Score</th>
                                <th className="table-list-th">Normalized</th>
                            </tr>
                        </thead>
                        <tbody aria-busy={loading} aria-live="polite">
                            {loading && <Skeleton.TableRows cols={['3xs', 'md', 'pill', 'pill']} />}
                            {!loading && filtered.length === 0 && (
                                <tr><td colSpan={4} className="table-list-cell"><NoDataFound title="No Rating Questions" message="No rating questions defined yet." /></td></tr>
                            )}
                            {!loading && paginatedRows.map((q, idx) => {
                                const opts = optionsByRating[q.id] || [];
                                return (
                                    <tr key={q.id} className="table-list-row" data-testid={`BTN_rating_template_row_${q.id}_open`} onClick={() => handleEdit(q)}>
                                        <td className="table-list-cell table-list-cell--sno">{(page - 1) * pageSize + idx + 1}</td>
                                        <td className="table-list-cell">
                                            <span className="table-list-cell-title">{q.rating_question}</span>
                                            <div className="ml-8 flex flex-wrap gap-2">
                                                {opts.map(opt => (
                                                    <span key={opt.id} className="status-badge xs neutral">
                                                        {opt.rating_option_text} <span className="ml-1.5 text-gray-400">({opt.rating_option_score})</span>
                                                    </span>
                                                ))}
                                                {opts.length === 0 && <span className="text-xs italic text-gray-400">No options yet</span>}
                                            </div>
                                        </td>
                                        <td className="table-list-cell">
                                            <span className="status-badge xs info">{q.maximum_score ?? 5}</span>
                                        </td>
                                        <td className="table-list-cell">
                                            {q.is_normalized ? (
                                                <span className="status-badge xs success">Yes</span>
                                            ) : (
                                                <span className="text-gray-400 text-xs">No</span>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
                <Pagination
                    page={page}
                    pageSize={pageSize}
                    totalRecords={totalRecords}
                    onPageChange={setPage}
                    onPageSizeChange={setPageSize}
                    recordLabel="questions"
                />
            </div>

            <Modal isOpen={isModalOpen} onClose={() => setModalOpen(false)} size="md">
                <Modal.Header>
                    {currentQ
                        ? <>Editing "<span className="item-highlight">{currentQ.rating_question}</span>"</>
                        : 'Create Rating Question'}
                </Modal.Header>
                <Modal.Body>
                    <form id="rating-item-form" data-testid="MOD_rating_template_form" onSubmit={handleSave} className="flex flex-col gap-y-4">
                        <div className="grid grid-cols-3 gap-x-6 gap-y-4">
                            <div className="col-span-2">
                                <FormControl.Input
                                    label="Question Text"
                                    required
                                    data-testid="INP_rating_template_question"
                                    placeholder="e.g. Financial Stability"
                                    value={formData.rating_question}
                                    onChange={e => setFormData({ ...formData, rating_question: e.target.value })}
                                    autoFocus
                                />
                            </div>
                            <FormControl.Input
                                label="Maximum Score"
                                type="number"
                                data-testid="INP_rating_template_max_score"
                                value={String(formData.maximum_score)}
                                onChange={e => setFormData({ ...formData, maximum_score: parseInt(e.target.value) || 0 })}
                            />
                        </div>
                        <FormControl.Checkbox
                            label="Normalize the score (divide by max)"
                            data-testid="CHK_rating_template_normalized"
                            checked={formData.is_normalized}
                            onChange={checked => setFormData({ ...formData, is_normalized: checked })}
                        />
                        <div>
                            <div className="flex justify-between items-center mb-2">
                                <span className="text-sm font-semibold text-gray-700">Rating Options</span>
                                <Button variant="fill" icon={Plus} data-testid="BTN_rating_template_option_add" onClick={addOption}>Create</Button>
                            </div>
                            <div className="space-y-2 bg-gray-50 p-3 rounded-lg border border-gray-100">
                                {formData.options.map((opt) => (
                                    <div key={opt.key} className="flex items-center gap-2">
                                        <div className="flex-1">
                                            <FormControl.Input
                                                placeholder="Option Text (e.g. Strong)"
                                                data-testid={`INP_rating_template_option_${opt.key}_text`}
                                                value={opt.rating_option_text}
                                                onChange={e => updateOption(opt.key, { rating_option_text: e.target.value })}
                                                noMargin
                                            />
                                        </div>
                                        <div className="w-20">
                                            <FormControl.Input
                                                type="number"
                                                placeholder="Score"
                                                data-testid={`INP_rating_template_option_${opt.key}_score`}
                                                value={String(opt.rating_option_score)}
                                                onChange={e => updateOption(opt.key, { rating_option_score: parseInt(e.target.value) || 0 })}
                                                noMargin
                                            />
                                        </div>
                                        <button
                                            type="button"
                                            data-testid={`BTN_rating_template_option_${opt.key}_remove`}
                                            onClick={() => removeOption(opt.key)}
                                            className={`p-2 rounded hover:bg-red-100 text-gray-400 hover:text-red-600 transition-colors ${formData.options.length === 1 ? 'opacity-50 cursor-not-allowed' : ''}`}
                                            disabled={formData.options.length === 1}
                                            title="Remove Option"
                                        >
                                            <MinusCircle size={18} />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </form>
                </Modal.Body>
                <Modal.Footer className={currentQ ? 'app-modal-footer--split' : ''}>
                    {currentQ && (
                        <Button
                            variant={(currentQ.is_active ?? 1) === 1 ? 'inactive-outline' : 'active-outline'}
                            icon={(currentQ.is_active ?? 1) === 1 ? CircleMinus : CircleCheck}
                            data-testid="BTN_rating_template_toggle_active"
                            onClick={handleToggleActive}
                            disabled={toggling || submitting}
                        >
                            {toggling ? '...' : (currentQ.is_active ?? 1) === 1 ? 'Inactive' : 'Active'}
                        </Button>
                    )}
                    <Button
                        type="submit"
                        form="rating-item-form"
                        variant={currentQ ? 'update' : 'fill'}
                        icon={currentQ ? Check : Save}
                        data-testid="BTN_rating_template_submit"
                        disabled={submitting || toggling || !formData.rating_question.trim()}
                    >
                        {submitting
                            ? currentQ ? 'Updating...' : 'Submitting...'
                            : currentQ ? 'Update' : 'Submit'}
                    </Button>
                </Modal.Footer>
            </Modal>

            <Toast toast={toast} onClose={() => setToast(null)} />
        </>
    );
};

export default ProcedureRating;
